import { useEffect, useRef } from "react";
import { confirmStore } from "../lib/confirm.ts";

/**
 * Renders the pending confirmation as a native <dialog>: real modal focus
 * trapping, Escape to cancel, and the page behind made inert.
 */
export function ConfirmHost() {
  const pending = confirmStore.use((s) => s.pending);
  const ref = useRef<HTMLDialogElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (pending && !d.open) {
      d.showModal();
      // Irreversible actions start on the safe choice.
      cancelRef.current?.focus();
    } else if (!pending && d.open) {
      d.close();
    }
  }, [pending]);

  const settle = (ok: boolean) => {
    pending?.resolve(ok);
    confirmStore.set({ pending: null });
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click is a pointer shortcut; Escape (onCancel) is the keyboard path
    <dialog
      ref={ref}
      className="confirm"
      aria-labelledby="confirm-title"
      aria-describedby={pending?.message ? "confirm-message" : undefined}
      onCancel={(e) => {
        e.preventDefault();
        settle(false);
      }}
      onClick={(e) => {
        // Click on the backdrop (the dialog element itself) cancels.
        if (e.target === e.currentTarget) settle(false);
      }}
    >
      {pending && (
        <div className="confirm__body">
          <h2 id="confirm-title">{pending.title}</h2>
          {pending.message && <p id="confirm-message">{pending.message}</p>}
          <div className="confirm__actions">
            <button
              ref={cancelRef}
              type="button"
              className="btn btn--white"
              onClick={() => settle(false)}
            >
              {pending.cancelLabel ?? "Cancel"}
            </button>
            <button
              type="button"
              className={`btn ${pending.tone === "danger" ? "btn--tomato" : ""}`}
              onClick={() => settle(true)}
            >
              {pending.confirmLabel}
            </button>
          </div>
        </div>
      )}
    </dialog>
  );
}
